import { useMemo } from "react";
import { CheckCircle, Lock, Play, BookOpen, Star, Award, Edit2, ExternalLink, X, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { useState } from "react";

interface CertificationDomain {
  certification: string;
  domain: string;
  objective?: string;
}

interface SkillNode {
  id: string;
  skill_tree_id: string;
  chapter_id?: string | null;
  title: string;
  description: string | null;
  level_index: number;
  position_index: number;
  is_key_node: boolean;
  content_summary: string | null;
  certification_domains?: CertificationDomain[];
}

interface SkillChapter {
  id: string;
  skill_tree_id: string;
  title: string;
  description?: string | null;
  order_index: number;
}

interface Prerequisite {
  id: string;
  skill_node_id: string;
  prereq_node_id: string;
}

interface InstructorSkillTreePreviewProps {
  nodes: SkillNode[];
  /** When provided, nodes are grouped by chapter instead of by level */
  chapters?: SkillChapter[];
  prerequisites: Prerequisite[];
  onNodeClick: (node: SkillNode) => void;
  treeTitle?: string;
  nodeContent?: Map<string, boolean>; // Map of nodeId -> hasContent
  nodeSlides?: Map<string, number>; // Map of nodeId -> slide count
  onConnectionHandleDragStart?: (e: React.DragEvent, nodeId: string) => void;
  onConnectionHandleDrop?: (e: React.DragEvent, nodeId: string) => void;
  onConnectionHandleDragEnd?: () => void;
  connectingFromHandleId?: string | null;
  onRemoveLink?: (fromNodeId: string, toNodeId: string) => void;
  /** Designer: delete a node from the preview (confirmation handled by parent). */
  onDeleteNode?: (node: SkillNode) => void;
  /** Designer: delete chapter and all its nodes (confirmation handled by parent). */
  onDeleteChapter?: (chapter: SkillChapter, nodesInChapter: SkillNode[]) => void;
}

const InstructorSkillTreePreview = ({ 
  nodes, 
  chapters = undefined,
  prerequisites, 
  onNodeClick, 
  treeTitle,
  nodeContent = new Map(),
  nodeSlides = new Map(),
  onConnectionHandleDragStart,
  onConnectionHandleDrop,
  onConnectionHandleDragEnd,
  connectingFromHandleId,
  onRemoveLink,
  onDeleteNode,
  onDeleteChapter,
}: InstructorSkillTreePreviewProps) => {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [dragHoverNodeId, setDragHoverNodeId] = useState<string | null>(null);

  const useChapters = chapters && chapters.length > 0 && nodes.some((n) => n.chapter_id);

  // When using chapters: group by chapter order; otherwise by level
  const chaptersWithNodes = useMemo(() => {
    if (!useChapters || !chapters) return [];
    const sorted = [...chapters].sort((a, b) => a.order_index - b.order_index);
    return sorted.map((ch) => ({
      chapter: ch,
      nodes: nodes
        .filter((n) => n.chapter_id === ch.id)
        .sort((a, b) => a.position_index - b.position_index),
    }));
  }, [useChapters, chapters, nodes]);

  const nodesByLevel = useMemo(() => {
    return nodes.reduce((acc, node) => {
      if (!acc[node.level_index]) {
        acc[node.level_index] = [];
      }
      acc[node.level_index].push(node);
      return acc;
    }, {} as Record<number, SkillNode[]>);
  }, [nodes]);

  const levels = useMemo(() => 
    Object.keys(nodesByLevel).map(Number).sort((a, b) => a - b),
    [nodesByLevel]
  );

  const displayGroups = useChapters
    ? chaptersWithNodes.map((g, i) => ({
        kind: "chapter" as const,
        index: i,
        title: g.chapter.title,
        chapter: g.chapter,
        nodes: g.nodes,
      }))
    : levels.map((levelIndex) => ({
        kind: "level" as const,
        index: levelIndex,
        title: `Level ${levelIndex + 1}`,
        chapter: undefined as SkillChapter | undefined,
        nodes: nodesByLevel[levelIndex].sort((a, b) => a.position_index - b.position_index),
      }));

  const getNodePosition = (node: SkillNode): { rowIndex: number; positionInRow: number } => {
    if (useChapters && chaptersWithNodes.length > 0) {
      for (let r = 0; r < chaptersWithNodes.length; r++) {
        const idx = chaptersWithNodes[r].nodes.findIndex((n) => n.id === node.id);
        if (idx >= 0) return { rowIndex: r, positionInRow: idx };
      }
    }
    const rowIndex = node.level_index;
    const levelNodes = nodesByLevel[rowIndex] || [];
    const positionInRow = levelNodes.sort((a, b) => a.position_index - b.position_index).findIndex((n) => n.id === node.id);
    return { rowIndex, positionInRow: positionInRow >= 0 ? positionInRow : 0 };
  };

  // Build prerequisite map for drawing connections
  const prereqMap = useMemo(() => {
    const map = new Map<string, string[]>();
    prerequisites.forEach(p => {
      if (!map.has(p.skill_node_id)) {
        map.set(p.skill_node_id, []);
      }
      map.get(p.skill_node_id)!.push(p.prereq_node_id);
    });
    return map;
  }, [prerequisites]);

  const getNodeStyles = (node: SkillNode) => {
    const baseStyles = "relative w-36 h-28 rounded-2xl flex flex-col items-center justify-center transition-all duration-300 shadow-lg border-2 cursor-pointer hover:scale-105 hover:shadow-xl group";
    
    const hasContent = nodeContent.get(node.id);
    const slideCount = nodeSlides.get(node.id) || 0;
    const isDragHovered = dragHoverNodeId === node.id;
    const canAcceptConnection = connectingFromHandleId && connectingFromHandleId !== node.id;
    
    // Highlight when dragging connection over this node
    if (canAcceptConnection && isDragHovered) {
      return cn(baseStyles, "ring-4 ring-primary ring-offset-2 scale-110 shadow-2xl border-primary bg-primary/10");
    }
    
    // Color based on content status for instructor view
    if (hasContent && slideCount > 0) {
      // Fully configured - green
      return cn(baseStyles, "bg-green-500/20 border-green-500", isDragHovered && canAcceptConnection && "ring-4 ring-primary");
    }
    if (hasContent || slideCount > 0) {
      // Partially configured - orange
      return cn(baseStyles, "bg-orange-500/20 border-orange-500", isDragHovered && canAcceptConnection && "ring-4 ring-primary");
    }
    if (node.is_key_node) {
      // Key node without content - purple
      return cn(baseStyles, "bg-purple-500/20 border-purple-500 ring-2 ring-purple-300/50", isDragHovered && canAcceptConnection && "ring-4 ring-primary");
    }
    // No content yet - primary
    return cn(baseStyles, "bg-primary/20 border-primary", isDragHovered && canAcceptConnection && "ring-4 ring-primary");
  };

  const getIcon = (node: SkillNode) => {
    const iconClass = "h-8 w-8";
    const hasContent = nodeContent.get(node.id);
    const slideCount = nodeSlides.get(node.id) || 0;
    
    if (hasContent && slideCount > 0) {
      return <CheckCircle className={cn(iconClass, "text-green-500")} />;
    }
    if (hasContent || slideCount > 0) {
      return <BookOpen className={cn(iconClass, "text-orange-500")} />;
    }
    if (node.is_key_node) {
      return <Star className={cn(iconClass, "text-purple-500")} />;
    }
    return <Play className={cn(iconClass, "text-primary")} />;
  };

  const getStatusText = (node: SkillNode) => {
    const hasContent = nodeContent.get(node.id);
    const slideCount = nodeSlides.get(node.id) || 0;
    
    if (hasContent && slideCount > 0) {
      return "Ready";
    }
    if (hasContent) {
      return "Has Lesson";
    }
    if (slideCount > 0) {
      return `${slideCount} Slides`;
    }
    return "Setup Needed";
  };

  const getStatusColor = (node: SkillNode) => {
    const hasContent = nodeContent.get(node.id);
    const slideCount = nodeSlides.get(node.id) || 0;
    
    if (hasContent && slideCount > 0) return "bg-green-500";
    if (hasContent || slideCount > 0) return "bg-orange-500";
    return "bg-muted";
  };


  return (
    <div className="w-full space-y-6">
      {/* Skill tree visualization */}
      <div className="relative overflow-x-auto pb-4">
        <div className="min-w-max py-4 px-4">
          {/* SVG for connection lines */}
          <svg 
            className="absolute inset-0 pointer-events-none" 
            style={{ minWidth: '100%', minHeight: '100%' }}
          >
            {displayGroups.map((group) =>
              group.nodes.map((node) => {
                const prereqs = prereqMap.get(node.id) || [];
                return prereqs.map((prereqId) => {
                  const prereqNode = nodes.find(n => n.id === prereqId);
                  if (!prereqNode) return null;

                  const prereqPos = getNodePosition(prereqNode);
                  const currPos = getNodePosition(node);

                  const prereqX = (prereqPos.positionInRow + 0.5) * 180 + 72;
                  const prereqY = prereqPos.rowIndex * 200 + 112;
                  const currentX = (currPos.positionInRow + 0.5) * 180 + 72;
                  const currentY = currPos.rowIndex * 200 + 24;

                  const hasContent = nodeContent.get(prereqNode.id);
                  const slideCount = nodeSlides.get(prereqNode.id) || 0;
                  const isReady = hasContent && slideCount > 0;

                  return (
                    <path
                      key={`${prereqId}-${node.id}`}
                      d={`M ${prereqX} ${prereqY} C ${prereqX} ${prereqY + 50}, ${currentX} ${currentY - 50}, ${currentX} ${currentY}`}
                      fill="none"
                      stroke={isReady ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"}
                      strokeWidth={isReady ? 3 : 2}
                      strokeOpacity={isReady ? 0.8 : 0.3}
                      strokeDasharray={isReady ? "none" : "6 6"}
                    />
                  );
                });
              })
            )}
          </svg>

          {/* Nodes by chapter or level */}
          <div className="relative z-10 space-y-16">
            {displayGroups.map((group, groupIndex) => {
              const groupNodes = group.nodes;
              const rowKey = group.kind === "chapter" && group.chapter ? group.chapter.id : `level-${group.index}`;
              return (
                <div key={rowKey} className="space-y-3">
                  <div className="text-sm font-semibold text-muted-foreground px-2 flex flex-wrap items-center gap-2">
                    <span className="bg-muted px-2 py-0.5 rounded">
                      {group.kind === "chapter" ? `Ch. ${groupIndex + 1}: ${group.title}` : `Level ${group.index + 1}`}
                    </span>
                    <span className="text-xs font-normal">
                      {groupNodes.length} {groupNodes.length === 1 ? 'node' : 'nodes'}
                    </span>
                    {group.kind === "chapter" && group.chapter && onDeleteChapter && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDeleteChapter(group.chapter!, groupNodes);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Delete chapter
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-12">
                    {groupNodes.map((node) => {
                      const nodePrereqs = prereqMap.get(node.id) || [];
                      
                      return (
                        <Tooltip key={node.id}>
                            <TooltipTrigger asChild>
                              <div
                                className="relative"
                                onMouseEnter={() => setHoveredNodeId(node.id)}
                                onMouseLeave={() => {
                                  setHoveredNodeId(null);
                                  if (!connectingFromHandleId) {
                                    setDragHoverNodeId(null);
                                  }
                                }}
                                onDragOver={(e) => {
                                  if (connectingFromHandleId && connectingFromHandleId !== node.id) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDragHoverNodeId(node.id);
                                  }
                                }}
                                onDragLeave={(e) => {
                                  // Only clear drag hover if we're actually leaving the node area
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const x = e.clientX;
                                  const y = e.clientY;
                                  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                                    setDragHoverNodeId(null);
                                  }
                                }}
                                onDrop={(e) => {
                                  setDragHoverNodeId(null);
                                  if (onConnectionHandleDrop) {
                                    onConnectionHandleDrop(e, node.id);
                                  }
                                }}
                              >
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => onNodeClick(node)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      onNodeClick(node);
                                    }
                                  }}
                                  className={getNodeStyles(node)}
                                >
                                  {/* Edit indicator on hover */}
                                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Edit2 className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                  {onDeleteNode && (
                                    <div
                                      className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-20"
                                      title="Delete node"
                                    >
                                      <Button
                                        type="button"
                                        variant="destructive"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          onDeleteNode(node);
                                        }}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                              
                                  {getIcon(node)}
                                  <span className="text-xs font-medium mt-1 line-clamp-2 px-2 text-center">
                                    {node.title}
                                  </span>
                                  
                                  {/* Status badge at bottom */}
                                  <Badge 
                                    variant="secondary" 
                                    className={cn(
                                      "absolute -bottom-2 text-[10px] px-1.5 py-0.5 text-white",
                                      getStatusColor(node)
                                    )}
                                  >
                                    {getStatusText(node)}
                                  </Badge>
                                  
                                  {/* Key node indicator */}
                                  {node.is_key_node && (
                                    <Badge 
                                      variant="secondary" 
                                      className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 bg-purple-500 text-white"
                                    >
                                      Exam
                                    </Badge>
                                  )}
                                  
                                  {/* Certification badges */}
                                  {node.certification_domains && node.certification_domains.length > 0 && (
                                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-0.5">
                                      {node.certification_domains.slice(0, 2).map((cd, i) => (
                                        <div 
                                          key={i}
                                          className="w-4 h-4 rounded-full bg-yellow-500 flex items-center justify-center"
                                          title={`${cd.certification}: ${cd.domain}`}
                                        >
                                          <Award className="h-2.5 w-2.5 text-white" />
                                        </div>
                                      ))}
                                      {node.certification_domains.length > 2 && (
                                        <div className="w-4 h-4 rounded-full bg-yellow-500/60 flex items-center justify-center text-[8px] text-white font-bold">
                                          +{node.certification_domains.length - 2}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Connection handle - Figma style draggable point */}
                                <div
                                  draggable
                                  onDragStart={(e) => {
                                    if (onConnectionHandleDragStart) {
                                      onConnectionHandleDragStart(e, node.id);
                                    }
                                  }}
                                  onDragEnd={() => {
                                    if (onConnectionHandleDragEnd) {
                                      onConnectionHandleDragEnd();
                                    }
                                  }}
                                  className={cn(
                                    "absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 bg-white cursor-grab active:cursor-grabbing z-10",
                                    "hover:scale-125 hover:bg-primary hover:border-primary transition-all",
                                    connectingFromHandleId === node.id && "bg-primary border-primary ring-2 ring-primary/50"
                                  )}
                                  onClick={(e) => e.stopPropagation()}
                                  title="Drag to connect to another node"
                                />
                                
                                {/* Remove link buttons when hovering */}
                                {nodePrereqs.length > 0 && hoveredNodeId === node.id && onRemoveLink && (
                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-1 z-20">
                                    {nodePrereqs.map((prereqId) => {
                                      const prereqNode = nodes.find(n => n.id === prereqId);
                                      if (!prereqNode) return null;
                                      return (
                                        <Tooltip key={prereqId}>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="destructive"
                                              size="sm"
                                              className="h-6 w-6 p-0"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onRemoveLink(prereqId, node.id);
                                              }}
                                            >
                                              <X className="h-3 w-3" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            Remove link from {prereqNode.title}
                                          </TooltipContent>
                                        </Tooltip>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            <div className="space-y-2">
                              <div className="font-semibold flex items-center gap-2">
                                {node.title}
                                <Edit2 className="h-3 w-3 text-muted-foreground" />
                              </div>
                              {node.description && (
                                <p className="text-sm text-muted-foreground">{node.description}</p>
                              )}
                              <div className="pt-2 border-t text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span>Lesson Content:</span>
                                  <span className={nodeContent.get(node.id) ? "text-green-500" : "text-muted-foreground"}>
                                    {nodeContent.get(node.id) ? "✓ Created" : "Not created"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Slides:</span>
                                  <span className={(nodeSlides.get(node.id) || 0) > 0 ? "text-green-500" : "text-muted-foreground"}>
                                    {(nodeSlides.get(node.id) || 0) > 0 ? `✓ ${nodeSlides.get(node.id)} deck(s)` : "Not created"}
                                  </span>
                                </div>
                                {node.is_key_node && (
                                  <div className="flex justify-between">
                                    <span>Exam:</span>
                                    <span className="text-purple-500">Key Node</span>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-primary pt-1">Click to edit</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorSkillTreePreview;

